import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { SectionHeader } from "@/components/brand/SectionHeader";

/**
 * A titled panel whose rows run full-bleed to its edge.
 *
 * The Card is p-0 on purpose: padding belongs to the rows, so a row can paint
 * its own background edge to edge when hovered or highlighted.
 */
export function Panel({
  icon,
  title,
  action,
  children,
  className,
  bodyClassName,
}: {
  icon?: React.ElementType;
  title?: React.ReactNode;
  /** Sits at the right of the header: a collapse toggle, a link, a count. */
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <Card className={cn("overflow-hidden p-0", className)}>
      {title && (
        <div className="flex items-center justify-between gap-2 border-b border-border px-5 py-3.5">
          <SectionHeader icon={icon}>{title}</SectionHeader>
          {action}
        </div>
      )}
      <div className={cn("divide-y divide-border", bodyClassName)}>
        {children}
      </div>
    </Card>
  );
}

/** One row of a {@link Panel}. The panel supplies the boundary, not the row. */
export function PanelRow({
  children,
  className,
  highlight = false,
}: {
  children: React.ReactNode;
  className?: string;
  /** Marks the row as the reader's own: their position, their bonus. */
  highlight?: boolean;
}) {
  return (
    <div
      className={cn(
        "px-5 py-4",
        highlight &&
          "relative bg-primary/[0.07] before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:bg-primary",
        className,
      )}
    >
      {children}
    </div>
  );
}
